"use client";

import { useEffect, useRef } from "react";
import type { OpenedTrade } from "../types";
import { isMarketTrade } from "../utils";
import { useOpenPositionsLiveMetricsStore } from "../stores/useOpenPositionsLiveMetricsStore";
import { useSubscribedSymbolsStore } from "../../subscribed-symbols/stores/useSubscribedSymbolsStore";
import { useSymbolsStore } from "../../symbols/stores/useSymbolsStore";

function buildSymbolIndex(trades: OpenedTrade[]): Map<string, OpenedTrade[]> {
  const index = new Map<string, OpenedTrade[]>();
  for (const trade of trades) {
    if (!isMarketTrade(trade)) continue;
    const list = index.get(trade.symbol) ?? [];
    list.push(trade);
    index.set(trade.symbol, list);
  }
  return index;
}

function getComputeContext(accountCurrency: string) {
  return {
    accountCurrency,
    symbols: useSymbolsStore.getState().byCurrency,
    quotes: useSubscribedSymbolsStore.getState().byCurrency,
  };
}

/** Keeps live metrics store in sync with trades + quotes without parent table subscribing to full quote map. */
export function useSyncOpenPositionsLiveMetrics(
  trades: OpenedTrade[],
  accountCurrency: string
): void {
  const indexRef = useRef<Map<string, OpenedTrade[]>>(new Map());
  const marketTradesRef = useRef<OpenedTrade[]>([]);

  useEffect(() => {
    marketTradesRef.current = trades.filter(isMarketTrade);
    indexRef.current = buildSymbolIndex(trades);
    const store = useOpenPositionsLiveMetricsStore.getState();
    store.syncTradeList(trades);
    store.recomputeAll(marketTradesRef.current, getComputeContext(accountCurrency));
  }, [trades, accountCurrency]);

  useEffect(() => {
    const unsubQuotes = useSubscribedSymbolsStore.subscribe((state, prev) => {
      const prevBy = prev?.byCurrency;
      if (!prevBy) return;

      const ctx = getComputeContext(accountCurrency);
      const store = useOpenPositionsLiveMetricsStore.getState();

      for (const [currency, entry] of state.byCurrency) {
        const quote = entry.quote;
        if (!quote) continue;
        const prevQuote = prevBy.get(currency)?.quote;
        if (
          prevQuote &&
          quote.bid === prevQuote.bid &&
          quote.ask === prevQuote.ask &&
          quote.last === prevQuote.last
        ) {
          continue;
        }
        const symbolTrades = indexRef.current.get(currency);
        if (!symbolTrades?.length) continue;
        store.recomputeForSymbol(currency, symbolTrades, ctx);
      }
    });

    const unsubSymbols = useSymbolsStore.subscribe((state, prev) => {
      if (state.byCurrency === prev?.byCurrency) return;
      useOpenPositionsLiveMetricsStore
        .getState()
        .recomputeAll(marketTradesRef.current, getComputeContext(accountCurrency));
    });

    return () => {
      unsubQuotes();
      unsubSymbols();
    };
  }, [accountCurrency]);
}
